<template>
	<c-input-element
		:label="label"
		:helptext="helptext"
		:class="{ 'c-form-element--horizontal' : horizontal }"
		:name="name"
	>
		<div class="c-select-search" @click.stop>
			<input
				ref="searchInput"
				type="text"
				class="c-input"
				:placeholder="placeholder"
				v-model="searchTerm"
				@focus="handleFocus"
				@blur="handleBlur"
			/>

			<ul v-if="focused" class="c-select-options">
				<li
					v-if="filteredOptions.length"
					v-for="option in filteredOptions"
					:key="option.id"
					class="option"
					@mousedown.prevent="selectOption(option)"
				>
					{{ getName(DisplayName ?? name, option) }}
				</li>
				<li
					v-else
					class="option option--disabled"
				>
					Geen resultaten
				</li>
			</ul>
		</div>
	</c-input-element>
</template>

<script>
import Input from '@mixins/Input';

export default {
	name: 'form-select-search-input',
	inheritAttrs: false,
	mixins: [Input],
	props: {
		placeholder: {
			type: String,
			default: 'Zoek en selecteer een optie'
		},
		options: {
			type: Array,
			required: true
		},
		name: {
			required: true
		},
		ValueName: {
			default: 'name'
		},
		DisplayName: {
			default: null
		},
		required: {
			type: Boolean,
			default: false
		},
		whiteBg: {
			type: Boolean,
			default: false
		},
		modelValue: {
			default: ''
		},
		displaySeperator: {
			type: Boolean,
			default: false
		},
	},
	emits: ['update:modelValue'],
	data() {
		return {
			searchTerm: '',
			focused: false,
			lastValidOption: null,
		};
	},
	computed: {
		filteredOptions() {
			const term = this.searchTerm.toLowerCase();
			return this.options.filter(option =>
				this.getName(this.DisplayName ?? this.name, option)
					.toLowerCase()
					.includes(term)
			);
		}
	},
	methods: {
		handleFocus() {
			this.focused = true;

			this.searchTerm = '';

			this.$nextTick(() => {
				this.$refs.searchInput?.select();
			});
		},
		handleBlur() {
			setTimeout(() => {
				const match = this.options.find(opt =>
					this.getName(this.DisplayName ?? this.name, opt) === this.searchTerm
				);

				if (match) {
					this.$emit('update:modelValue', this.getName(this.ValueName, match));
					this.lastValidOption = match;
				} else if (this.lastValidOption) {
					this.searchTerm = this.getName(this.DisplayName ?? this.name, this.lastValidOption);
					this.$emit('update:modelValue', this.getName(this.ValueName, this.lastValidOption));
				} else {
					this.searchTerm = '';
					this.$emit('update:modelValue', '');
				}

				this.focused = false;
			}, 150);
		},
		selectOption(option) {
			const value = this.getName(this.ValueName, option);
			this.$emit('update:modelValue', value);
			this.searchTerm = this.getName(this.DisplayName ?? this.name, option);
			this.lastValidOption = option;
			this.focused = false;
			this.$refs.searchInput?.blur();
		},
		getName(name, option) {
			if (!option) return '';

			if (typeof name === 'function') {
				return name(option);
			}

			if (typeof name === 'string' && name) {
				const parts = name.split(',');
				return parts
					.map(n => n.match(/^'(.*)'$/)?.[1] ?? option[n.trim()])
					.join(this.displaySeperator ? ' | ' : ' ');
			}

			return option;
		}
	},
	watch: {
		modelValue: {
			immediate: true,
			handler(newVal) {
				if (!Array.isArray(this.options)) return;
				const match = this.options.find(opt => this.getName(this.ValueName, opt) === newVal);
				if (match) {
					this.searchTerm = this.getName(this.DisplayName ?? this.name, match);
					this.lastValidOption = match;
				}
			}
		}
	}
};
</script>

<style scoped>
.c-select-search {
	position: relative;
	background-color: #f1f1f1;
}

.c-select-options {
	position: absolute;
	z-index: 10;
	left: 0;
	top: 48px;
	width: 100%;
	margin: 0;
	background-color: white;
	border: 1px solid #ccc;
	max-height: 400px;
	overflow-y: auto;
	box-shadow: 0 4px 8px rgba(0, 0, 0, 0.05);
}

.c-select-options .option {
	padding: 8px 12px;
	cursor: pointer;
	transition: background-color 0.2s ease;
}

.c-select-options .option:hover {
	background-color: #f5f5f5;
}

input.c-input {
	width: 100%;
}
</style>